import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  FileDownloadIcon,
  PageHeader,
  Select,
  SelectItem,
  SelectTrigger,
  SortIcon,
  Spinner,
  Text,
  cn,
  ledgerContainerClass,
} from '@billda/design-system';
import { Permissions, usePermission } from '@billda/permissions-ui';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { SidePanel, ResizableHandle } from '../../../shared/components/panel';
import { useSitePanelState } from '../../sites/hooks/useSitePanelState';
import { useActivitySites } from '../hooks/useActivitySites';
import { ACTIVITY_PAGE_SIZE, useActivityList } from '../hooks/useActivityList';
import { useActivityUIStore } from '../stores/activityUIStore';
import { DateRangeControl } from '../components/DateRangeControl';
import { ActivityFilterContent } from '../components/ActivityFilterContent';
import { ExportActivityModal } from '../components/ExportActivityModal';
import { BookingDetailsPanel } from '../../sites/components/panel/organisms/BookingDetailsPanel';
import { ActivityLedger } from '../ledger/ActivityLedger';
import { ACTIVITY_COLLAPSE } from '../ledger/columns';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';
import {
  toActivityRows,
  type ActivityLedgerRowModel,
} from '../ledger/toActivityRows';
import type { ActivityRow, ActivitySortDirection } from '../types';

const MS_DAY = 86_400_000;

const STATUS_LABELS: Record<string, string> = {
  Booked: 'Booked',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Cancelled: 'Cancelled',
};

type ActivityView = 'all' | 'attention';

function ActivityAllocationPanel({
  allocationId,
  onReviewSuccess,
}: {
  allocationId: string;
  onReviewSuccess: () => void;
}) {
  return (
    <BookingDetailsPanel
      allocationId={allocationId}
      onReviewSuccess={onReviewSuccess}
    />
  );
}

export default function ActivityPage() {
  const { isOpen, id, type, openPanel, closePanel } = useSitePanelState();
  const ui = useActivityUIStore();
  const canApprove = usePermission(Permissions.Labour.Approve);
  const { data: sites = [] } = useActivitySites();
  const [exportOpen, setExportOpen] = useState(false);
  const [view, setView] = useState<ActivityView>('all');
  const [sortDirection, setSortDirection] =
    useState<ActivitySortDirection>('Descending');
  const [page, setPage] = useState(0);
  // Track only what the user has explicitly collapsed, so every week opens by
  // default.
  const [collapsedWeeks, setCollapsedWeeks] = useState<ReadonlySet<string>>(
    new Set()
  );

  const filters = {
    status: view === 'all' ? ui.statusFilter : '',
    type: ui.typeFilter,
    siteIds: ui.siteIdsFilter,
    from: view === 'all' ? ui.fromDate : '',
    to: view === 'all' ? ui.toDate : '',
    reviewableOnly: view === 'attention',
    sortDirection,
  };

  const { data, isLoading, error, refetch } = useActivityList(filters, page);
  const { data: attentionData } = useActivityList(
    {
      status: '',
      type: ui.typeFilter,
      siteIds: ui.siteIdsFilter,
      from: '',
      to: '',
      reviewableOnly: true,
      sortDirection: 'Ascending',
    },
    0,
    1,
    canApprove
  );

  const items = useMemo(() => data?.items ?? [], [data]);
  const totalCount = data?.totalCount;
  const weeks = useMemo(
    () => toActivityRows(items, sortDirection),
    [items, sortDirection]
  );

  useEffect(() => {
    setPage(0);
  }, [
    view,
    sortDirection,
    ui.statusFilter,
    ui.typeFilter,
    ui.siteIdsFilter,
    ui.fromDate,
    ui.toDate,
  ]);

  useEffect(() => {
    if (
      totalCount !== undefined &&
      page > 0 &&
      page * ACTIVITY_PAGE_SIZE >= totalCount
    ) {
      setPage(page - 1);
    }
  }, [page, totalCount]);

  const toggleWeek = (key: string) =>
    setCollapsedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const handleRowClick = (row: ActivityLedgerRowModel) =>
    openPanel(
      'allocation',
      row.id,
      row.siteId.toString(),
      row.plotId.toString()
    );

  const openItem = (item: ActivityRow) =>
    openPanel(
      'allocation',
      item.id,
      item.siteId.toString(),
      item.plotId.toString()
    );

  const handleReviewSuccess = () => {
    if (view !== 'attention' || !id) {
      return;
    }
    const currentIndex = items.findIndex(item => item.id === id);
    const next = items[currentIndex + 1] ?? items[currentIndex - 1];
    if (next) {
      openItem(next);
    } else {
      closePanel();
    }
  };

  const handleViewChange = (value: string) => {
    const next = value === 'attention' && canApprove ? 'attention' : 'all';
    setView(next);
    setSortDirection(next === 'attention' ? 'Ascending' : 'Descending');
  };

  const isFilterActive =
    (view === 'all' && ui.statusFilter !== '') ||
    ui.typeFilter !== '' ||
    ui.siteIdsFilter.length > 0;

  const filterChips = (
    <>
      {view === 'all' && ui.statusFilter && (
        <Chip
          size="sm"
          label={STATUS_LABELS[ui.statusFilter] ?? ui.statusFilter}
          onDelete={() => {
            ui.setStatusFilter('');
          }}
          className="h-6.5 rounded-lg"
        />
      )}
      {ui.typeFilter && (
        <Chip
          size="sm"
          label={ui.typeFilter === 'Labour' ? 'Labour' : 'Variations'}
          onDelete={() => {
            ui.setTypeFilter('');
          }}
          className="h-6.5 rounded-lg"
        />
      )}
      {ui.siteIdsFilter.map(siteId => {
        const site = sites.find(s => s.id === siteId);
        return (
          <Chip
            key={siteId}
            size="sm"
            label={site?.name ?? `Site ${siteId}`}
            onDelete={() => {
              ui.toggleSiteId(siteId);
            }}
            className="h-6.5 rounded-lg"
          />
        );
      })}
    </>
  );

  const panelContent = (
    <>
      {isOpen && type === 'allocation' && id && (
        <>
          <ResizableHandle />
          <SidePanel open={isOpen} onClose={closePanel} title="Booking details">
            <ActivityAllocationPanel
              allocationId={id}
              onReviewSuccess={handleReviewSuccess}
            />
          </SidePanel>
        </>
      )}
    </>
  );

  return (
    <PageContentArea isPanelOpen={isOpen} panel={panelContent}>
      <PageHeader
        title="Activity"
        actions={
          <Button
            size="sm"
            variant="ghost"
            className="text-content-secondary"
            onClick={() => {
              setExportOpen(true);
            }}
            data-testid="activity-export"
          >
            <FileDownloadIcon aria-hidden="true" className="size-3.5" />
            <span className="hidden sm:inline">Export</span>
          </Button>
        }
      />
      <PageToolbar
        tabs={
          <div className="flex w-full flex-wrap items-center gap-x-4 gap-y-1">
            <ChipTabs
              tabs={[
                { label: 'All activity', value: 'all' },
                ...(canApprove
                  ? [
                      {
                        label: 'Needs approval',
                        value: 'attention',
                        count: attentionData?.totalCount,
                      },
                    ]
                  : []),
              ]}
              value={view}
              onChange={handleViewChange}
              data-testid="activity-view-tabs"
            />
            <div className="ml-auto flex shrink-0 items-center gap-3">
              {view === 'all' && (
                <DateRangeControl
                  from={ui.fromDate}
                  to={ui.toDate}
                  onChange={ui.setDateRange}
                />
              )}
              <div className="flex items-center gap-1">
                <SortIcon
                  aria-hidden="true"
                  className="size-[18px] text-content-secondary"
                />
                <Select
                  value={sortDirection}
                  onValueChange={value => {
                    setSortDirection(value as ActivitySortDirection);
                  }}
                >
                  <SelectTrigger
                    aria-label="Sort activity"
                    data-testid="activity-sort"
                    className="h-8 min-w-30 border-0 bg-transparent px-2 type-body-md text-content-secondary"
                  />
                  <SelectItem value="Descending">Newest first</SelectItem>
                  <SelectItem value="Ascending">Oldest first</SelectItem>
                </Select>
              </div>
            </div>
          </div>
        }
        isFilterActive={isFilterActive}
        filterChips={filterChips}
        filterContent={
          <ActivityFilterContent
            status={ui.statusFilter}
            onStatusChange={ui.setStatusFilter}
            siteIds={ui.siteIdsFilter}
            onSitesChange={ui.setSiteIds}
            type={ui.typeFilter}
            onTypeChange={ui.setTypeFilter}
            showStatus={view === 'all'}
          />
        }
      />

      <div
        className={cn(
          'flex-1 overflow-y-auto',
          ledgerContainerClass(ACTIVITY_COLLAPSE.container)
        )}
      >
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <Alert severity="error" className="justify-center">
              Couldn&apos;t load activity.{' '}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  refetch();
                }}
              >
                Try again
              </Button>
            </Alert>
          </div>
        ) : weeks.length === 0 ? (
          <div className="p-12 text-center">
            <Text variant="h6" color="secondary" className="mb-2">
              {view === 'attention'
                ? 'Nothing awaiting approval'
                : 'No activity'}
            </Text>
            <Text variant="body2" color="secondary" className="mb-4">
              {view === 'attention'
                ? 'There is no booked work requiring your review.'
                : 'Nothing was booked for the current filters.'}
            </Text>
            <div className="flex justify-center gap-2">
              {view === 'all' && (ui.fromDate || ui.toDate) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const to = new Date();
                    const from = new Date(Date.now() - 29 * MS_DAY);
                    ui.setDateRange(from.toISOString(), to.toISOString());
                  }}
                >
                  Widen to last 30 days
                </Button>
              )}
              {isFilterActive && (
                <Button variant="ghost" size="sm" onClick={ui.clearFilters}>
                  Clear filters
                </Button>
              )}
            </div>
          </div>
        ) : (
          <ActivityLedger
            weeks={weeks}
            collapsedWeeks={collapsedWeeks}
            onToggleWeek={toggleWeek}
            onOpenRow={handleRowClick}
            totalCount={totalCount ?? 0}
            page={page}
            pageSize={ACTIVITY_PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </div>

      <ExportActivityModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        from={ui.fromDate}
        to={ui.toDate}
        siteIds={ui.siteIdsFilter}
        type={ui.typeFilter}
      />
    </PageContentArea>
  );
}
